/**
 * at_map e conformita' — sezione riservata al Super Admin.
 *
 * Il pannello fa dall'interno la verifica che TrasparenzAI fa dall'esterno:
 * scarica `at_map.json` **senza autenticazione**, esattamente come farebbe un
 * crawler, e la confronta con l'alberatura dell'ente. Se le due divergono, la
 * divergenza e' visibile qui prima che la rilevi qualcun altro.
 *
 * Due precisazioni sull'indirizzo interrogato, che non sono dettagli:
 *
 * 1. **La mappa si chiede per l'ente corrente.** Senza `?ente=` l'API ricade sul
 *    primo ente attivo (CONVENTIONS §9.1): in un'installazione multi-ente il
 *    confronto con l'alberatura mostrata a schermo darebbe ogni nodo come
 *    mancante e ogni URL come divergente.
 * 2. **La mappa che conta e' quella del portale.** Il crawler legge
 *    `PORTALE_BASE_URL/at_map.json`, servita dal dominio del Comune, non quella
 *    dell'API. Verificare solo la seconda puo' dichiarare conforme una
 *    situazione in cui la mappa pubblica e' un ripiego sulle fixture, quindi i
 *    due esiti restano distinti e dichiarati.
 *
 * La checklist pre-rilascio e' resa operativa: le voci verificabili dal client
 * hanno un esito calcolato, le altre restano marcate come accertamento manuale.
 * Spuntarle d'ufficio darebbe una falsa sicurezza, ed e' esattamente il motivo
 * per cui una checklist perde valore.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { ErroreApi, indirizzoPortale, leggiAtMap, leggiAtMapPortale } from "../api/client";
import type { AtMap } from "../api/types";
import { Avviso, Bottone, Chip, Scheda, Scheletro } from "../components/primitive";
import { useAlberatura } from "../dati/contesto-alberatura";
import { costruisciChecklist, diagnosticaAtMap, riepilogoChecklist } from "../lib/conformita";
import type { EsitoVerifica } from "../lib/conformita";
import { useRisorsa } from "../lib/useRisorsa";
import { IntestazionePagina } from "../layout/Guscio";

const TONO_ESITO: Record<EsitoVerifica, "positivo" | "critico" | "attenzione" | "neutro"> = {
  conforme: "positivo",
  divergente: "critico",
  manuale: "attenzione",
  sconosciuto: "neutro",
};

const ETICHETTA_ESITO: Record<EsitoVerifica, string> = {
  conforme: "Conforme",
  divergente: "Divergente",
  manuale: "Verifica manuale",
  sconosciuto: "Non verificabile",
};

export function Conformita() {
  const { ente, nodi, ricarica: ricaricaAlberatura } = useAlberatura();
  const enteId = ente?.id ?? null;

  const carica = useCallback(
    (segnale: AbortSignal) => leggiAtMap(enteId, segnale),
    [enteId],
  );
  const { dato, inCaricamento, errore, ricarica } = useRisorsa<AtMap>(carica, [enteId]);

  // Lettura «da crawler»: stesso file, ma sul dominio del portale.
  const caricaDalPortale = useCallback(
    (segnale: AbortSignal) => leggiAtMapPortale(segnale),
    [],
  );
  const { dato: atMapPortale, inCaricamento: portaleInCaricamento, ricarica: ricaricaPortale } =
    useRisorsa<AtMap | null>(caricaDalPortale, []);
  const urlPortale = indirizzoPortale();

  const [soloDivergenze, setSoloDivergenze] = useState(false);

  useEffect(() => {
    document.title = "Conformita' · Amministrazione trasparente";
  }, []);

  const diagnosi = useMemo(
    () => diagnosticaAtMap(dato, errore ? erroreLeggibile(errore) : null, nodi),
    [dato, errore, nodi],
  );
  const voci = useMemo(() => costruisciChecklist(diagnosi, nodi), [diagnosi, nodi]);

  /**
   * Confronto fra la mappa dell'API e quella servita dal portale.
   *
   * Sono due file distinti: la seconda e' quella che TrasparenzAI scarica. Se
   * divergono, la verifica interna dice una cosa e il crawler ne vede un'altra.
   */
  const confrontoPortale = useMemo(() => {
    if (!urlPortale || !dato || !atMapPortale) return null;
    const chiaviApi = Object.keys(dato);
    const mancanti = chiaviApi.filter((chiave) => !(chiave in atMapPortale));
    const divergenti = chiaviApi.filter(
      (chiave) => chiave in atMapPortale && atMapPortale[chiave] !== dato[chiave],
    );
    return { voci: Object.keys(atMapPortale).length, mancanti, divergenti };
  }, [urlPortale, dato, atMapPortale]);
  const riepilogo = riepilogoChecklist(voci);
  const visibili = soloDivergenze ? voci.filter((voce) => voce.esito === "divergente") : voci;

  const sezioni = [...new Set(visibili.map((voce) => voce.sezione))];

  return (
    <div className="pila">
      <IntestazionePagina
        sopratitolo="Amministrazione"
        titolo="at_map e conformita'"
        sottotitolo={`Stato della mappa dei link e checklist pre-rilascio${
          ente ? ` per ${ente.denominazione}` : ""
        }. La at_map e' letta senza autenticazione, come farebbe un crawler, sia dall'API sia dall'indirizzo pubblico del portale.`}
        azioni={
          <Bottone
            variante="contorno"
            onClick={() => {
              ricarica();
              ricaricaPortale();
              ricaricaAlberatura();
            }}
          >
            Rilancia le verifiche
          </Bottone>
        }
      />

      <div className="griglia-kpi">
        <div className={`kpi kpi--${diagnosi.raggiungibile ? "positivo" : "critico"}`}>
          <div className="kpi__etichetta">Stato della at_map</div>
          <div className="kpi__valore" style={{ fontSize: "1.4rem" }}>
            {inCaricamento ? "…" : diagnosi.raggiungibile ? "raggiungibile" : "non raggiungibile"}
          </div>
          <div className="kpi__nota">
            {diagnosi.raggiungibile
              ? `${diagnosi.voci_totali} voci pubblicate in radice di dominio.`
              : (diagnosi.errore ?? "")}
          </div>
        </div>
        <div className={`kpi kpi--${diagnosi.nodi_mancanti.length === 0 ? "positivo" : "critico"}`}>
          <div className="kpi__etichetta">Nodi mancanti nella at_map</div>
          <div className="kpi__valore">{diagnosi.nodi_mancanti.length}</div>
          <div className="kpi__nota">Nodi dell'alberatura senza voce corrispondente.</div>
        </div>
        <div className={`kpi kpi--${diagnosi.chiavi_orfane.length === 0 ? "positivo" : "attenzione"}`}>
          <div className="kpi__etichetta">Voci orfane</div>
          <div className="kpi__valore">{diagnosi.chiavi_orfane.length}</div>
          <div className="kpi__nota">Voci della at_map senza nodo nell'alberatura.</div>
        </div>
        <div className={`kpi kpi--${riepilogo.divergente === 0 ? "positivo" : "critico"}`}>
          <div className="kpi__etichetta">Voci di checklist divergenti</div>
          <div className="kpi__valore">{riepilogo.divergente}</div>
          <div className="kpi__nota">
            {riepilogo.conforme} conformi · {riepilogo.manuale} da verificare a mano.
          </div>
        </div>
      </div>

      {/* Verifica «da crawler»: la mappa che TrasparenzAI legge davvero e' quella
          servita dal dominio del portale, non quella dell'API. */}
      {!urlPortale ? (
        <Avviso tono="attenzione" titolo="Verifica da crawler non eseguita">
          L'indirizzo pubblico del portale non e' configurato (<code>VITE_PORTALE_BASE_URL</code>):
          la mappa qui sopra e' quella dell'API. Quella che TrasparenzAI scarica e'{" "}
          <code>PORTALE_BASE_URL/at_map.json</code>, e non e' stata verificata.
        </Avviso>
      ) : portaleInCaricamento ? null : !atMapPortale ? (
        <Avviso tono="critico" titolo="at_map del portale non raggiungibile">
          <code>{urlPortale}/at_map.json</code> non ha risposto. E' l'indirizzo che il crawler
          scansiona: finche' non risponde, la sezione risulta priva di mappa dei link
          indipendentemente da quanto espone l'API.
        </Avviso>
      ) : confrontoPortale &&
        (confrontoPortale.mancanti.length > 0 || confrontoPortale.divergenti.length > 0) ? (
        <Avviso tono="critico" titolo="La mappa pubblica diverge da quella dell'API">
          <code>{urlPortale}/at_map.json</code> espone {confrontoPortale.voci} voci:{" "}
          {confrontoPortale.mancanti.length} nodi dell'API vi mancano e{" "}
          {confrontoPortale.divergenti.length} hanno un indirizzo diverso. E' la mappa pubblica a
          fare fede per la verifica automatica: la conformita' dichiarata qui sopra non basta.
          {confrontoPortale.mancanti.length > 0 ? (
            <>
              {" "}
              Mancanti: <span className="dato">{confrontoPortale.mancanti.join(", ")}</span>.
            </>
          ) : null}
        </Avviso>
      ) : confrontoPortale ? (
        <Avviso tono="positivo" titolo="Mappa pubblica allineata">
          <code>{urlPortale}/at_map.json</code> espone le stesse {confrontoPortale.voci} voci
          dell'API, con gli stessi indirizzi: e' quanto legge il crawler.
        </Avviso>
      ) : null}

      {errore ? (
        <Avviso tono="critico" titolo="at_map non leggibile">
          {erroreLeggibile(errore)} L'indirizzo atteso e' <code>/at_map.json</code> in radice di
          dominio, accessibile pubblicamente e dichiarato nel <code>robots.txt</code> con la
          direttiva <code>ATmap:</code>.
        </Avviso>
      ) : null}

      {diagnosi.nodi_mancanti.length > 0 ? (
        <Scheda
          titolo="Nodi assenti dalla at_map"
          descrizione="Ogni nodo dell'alberatura deve avere una voce nella mappa dei link: e' la corrispondenza fra URL e obbligo di pubblicazione."
          fitto
        >
          <div className="tabella-guscio">
            <table className="tabella">
              <thead>
                <tr>
                  <th scope="col">Identificativo</th>
                  <th scope="col">Denominazione</th>
                  <th scope="col">URL atteso</th>
                </tr>
              </thead>
              <tbody>
                {diagnosi.nodi_mancanti.map((nodo) => (
                  <tr key={nodo.id_nodo}>
                    <td className="dato">{nodo.id_nodo}</td>
                    <td className="tabella__principale">{nodo.denominazione}</td>
                    <td className="dato testo-tenue">{nodo.url || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Scheda>
      ) : null}

      {diagnosi.url_divergenti.length > 0 ? (
        <Scheda
          titolo="URL divergenti"
          descrizione="La at_map dichiara un indirizzo diverso da quello calcolato per il nodo: gli URL sono stabili per contratto."
          fitto
        >
          <div className="tabella-guscio">
            <table className="tabella">
              <thead>
                <tr>
                  <th scope="col">Nodo</th>
                  <th scope="col">URL nell'alberatura</th>
                  <th scope="col">URL nella at_map</th>
                </tr>
              </thead>
              <tbody>
                {diagnosi.url_divergenti.map((divergenza) => (
                  <tr key={divergenza.id_nodo}>
                    <td className="dato">{divergenza.id_nodo}</td>
                    <td className="dato testo-tenue">{divergenza.url_alberatura}</td>
                    <td className="dato campo__errore">{divergenza.url_at_map}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Scheda>
      ) : null}

      {/* Divergenza di POSIZIONE: l'indirizzo pubblicato non rispecchia la
          catena degli antenati. Non la vede il confronto con la colonna `url`,
          che della stessa colonna e' figlia: qui il termine di paragone e'
          l'alberatura. */}
      {diagnosi.url_non_canonici.length > 0 ? (
        <Scheda
          titolo="URL incoerenti con l'alberatura"
          descrizione="L'indirizzo pubblicato non corrisponde alla posizione del nodo: il portale valida l'intera catena, quindi la at_map sta pubblicando un collegamento che non si risolve."
          fitto
        >
          <div className="tabella-guscio">
            <table className="tabella">
              <thead>
                <tr>
                  <th scope="col">Nodo</th>
                  <th scope="col">URL pubblicato</th>
                  <th scope="col">Percorso atteso dalla posizione</th>
                </tr>
              </thead>
              <tbody>
                {diagnosi.url_non_canonici.map((incoerenza) => (
                  <tr key={incoerenza.id_nodo}>
                    <td className="dato">{incoerenza.id_nodo}</td>
                    <td className="dato campo__errore">{incoerenza.url_pubblicato}</td>
                    <td className="dato testo-tenue">{incoerenza.url_atteso}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Scheda>
      ) : null}

      <Scheda
        titolo="Checklist pre-rilascio"
        descrizione="Voci verificabili automaticamente e voci che restano di accertamento umano."
        azioni={
          <Bottone
            variante={soloDivergenze ? "primario" : "contorno"}
            dimensione="piccolo"
            onClick={() => setSoloDivergenze((valore) => !valore)}
            aria-pressed={soloDivergenze}
          >
            Solo divergenze
          </Bottone>
        }
        fitto
      >
        {inCaricamento ? (
          <div style={{ padding: "1.25rem" }}>
            <Scheletro righe={8} />
          </div>
        ) : visibili.length === 0 ? (
          <p className="campo__aiuto" style={{ padding: "1.25rem" }}>
            Nessuna divergenza rilevata fra le voci verificabili automaticamente.
          </p>
        ) : (
          <div className="tabella-guscio">
            <table className="tabella">
              <thead>
                <tr>
                  <th scope="col">Verifica</th>
                  <th scope="col">Esito</th>
                  <th scope="col">Dettaglio</th>
                  <th scope="col">Riferimento</th>
                </tr>
              </thead>
              {sezioni.map((sezione) => (
                <tbody key={sezione}>
                  <tr>
                    <th scope="colgroup" colSpan={4} style={{ position: "static" }}>
                      {sezione}
                    </th>
                  </tr>
                  {visibili
                    .filter((voce) => voce.sezione === sezione)
                    .map((voce) => (
                      <tr key={voce.chiave}>
                        <td className="tabella__principale">{voce.descrizione}</td>
                        <td>
                          <Chip tono={TONO_ESITO[voce.esito]}>{ETICHETTA_ESITO[voce.esito]}</Chip>
                        </td>
                        <td className="testo-tenue">{voce.dettaglio}</td>
                        <td className="dato testo-fioco">{voce.riferimento}</td>
                      </tr>
                    ))}
                </tbody>
              ))}
            </table>
          </div>
        )}
      </Scheda>
    </div>
  );
}

function erroreLeggibile(errore: ErroreApi): string {
  return errore.nonTrovato
    ? "Il file at_map.json non risponde all'indirizzo pubblico atteso."
    : errore.messaggioEsteso;
}
